import React from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resolveColorTheme } from 'utils/colorThemeHelpers';

// Pill labels, same mapping as the landing's tab strip (EventPreview.js).
const localeLabel = (code) => (code === 'zh-Hans' ? '简' : code === 'zh-Hant' ? '繁' : 'En');

// Review N1 (PR55 relay): on the RSVP steps the pills do not select a panel —
// they change the language of the whole page. So this is a toggle group, not a
// tab set: role="group" on the container, aria-pressed on each pill, and an
// aria-label that names the target language for assistive tech.
const localeAriaLabel = (code) =>
  code === 'zh-Hans'
    ? 'Switch to Simplified Chinese'
    : code === 'zh-Hant'
    ? 'Switch to Traditional Chinese'
    : 'Switch to English';

// The landing's language switch, reused on every RSVP step after the landing.
//
// Deliberately holds NO local state: the active pill is derived from
// i18n.language each render, and a click runs i18n.changeLanguage and nothing
// else. That means an external language change (a step change, a ?locale=
// query) moves the pill too, and there is no state to fall out of sync. The
// operator ruled 2026-10-14 that nothing switches automatically — so mounting
// this component never touches the language.
//
// Renders nothing under 2 locales, matching the landing's showTabs threshold:
// a single-locale event must look exactly as it does today, and an empty
// bordered row would be a visible change to it.
const RsvpLanguageSwitch = (props) => {
  const { locales, record } = props;
  const { i18n } = useTranslation();

  if (!Array.isArray(locales) || locales.length < 2) {
    return null;
  }

  // Review F1 (PR60): fill a pill only when the page language is one of the
  // event's locales. The form body renders from i18n.language, so a fallback
  // fill would name a language the form is not showing — e.g. ?locale=zh-Hans
  // on an en+zh-Hant event renders 简体 text under a filled En pill. The
  // landing avoids that split because its card follows selectedLocale; the
  // form has no such buffer. No pill filled is the honest state, and a click
  // brings pill and page back together. Mounting still never switches the
  // language on its own (operator ruling 2026-10-14).
  const activeLocale = locales.includes(i18n.language) ? i18n.language : null;

  // Resolved here rather than threaded as a prop: every step already has the
  // record, and this is the same call the landing makes. An unthemed event
  // leaves these undefined and MUI falls back to palette.primary.main, which
  // register.js sets to the app teal — the unthemed landing look.
  const theme = resolveColorTheme(record);

  return (
    <Box
      role="group"
      aria-label="Language"
      data-testid="rsvp-lang-switch"
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        px: 2,
        py: 1.25,
        fontSize: 12,
      }}
    >
      {locales.map((l) => {
        const active = l === activeLocale;
        return (
          <Button
            key={l}
            size="small"
            variant={active ? 'contained' : 'outlined'}
            aria-pressed={active}
            aria-label={localeAriaLabel(l)}
            data-testid={`rsvp-lang-${l}`}
            onClick={() => {
              i18n.changeLanguage(l);
            }}
            sx={{
              minWidth: 44,
              py: 0.25,
              px: 1.5,
              fontSize: 12,
              fontWeight: active ? 700 : 400,
              textTransform: 'none',
              ...(active
                ? {
                    backgroundColor: theme.linkButtonColor,
                    color: theme.buttonTextColor,
                    '&:hover': { backgroundColor: theme.linkButtonColor },
                  }
                : {
                    borderColor: theme.linkButtonColor,
                    color: theme.linkButtonColor,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderColor: theme.linkButtonColor,
                    },
                  }),
            }}
          >
            {localeLabel(l)}
          </Button>
        );
      })}
    </Box>
  );
};

export default RsvpLanguageSwitch;
